import { useId, useState, useEffect, useMemo } from "react";
import { Loader2, ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";
import { submitLead, capturesCountry, getSourceContext } from "@/utils/leadService";
import { LEAD_FORM } from "@/constants/testIds";
import { useCourses, getCoursesByCategory } from "@/utils/courseService";
import { COURSE_CATEGORIES } from "@/data/courses";

// Sentinel used by the Contact Us page's Category → Course cascade (see
// `isAdaptive` below) when the visitor's course isn't in the catalog dropdown
// -- selecting it reveals a free-text input instead.
const OTHER_COURSE = "__other__";

// Friendly label for a course `category` id, reusing the same titles shown
// on the /courses page filter where one exists.
const CATEGORY_LABELS = Object.fromEntries(COURSE_CATEGORIES.map((c) => [c.id, c.title]));
function categoryLabel(id) {
  return CATEGORY_LABELS[id] || (id ? id.charAt(0).toUpperCase() + id.slice(1) : id);
}

// The Corporate form's existing "Training Category" dropdown uses its own
// plain-English labels (not course `category` ids) -- map them so the new
// Course field can look up catalog courses for whichever one is selected.
// "AI for Leaders" has no catalog courses (those are separate leadership
// programs), so it always falls through to the free-text "Other" option.
const TRAINING_CATEGORY_TO_COURSE_CATEGORY = {
  SAP: "sap",
  Workday: "workday",
  "AI for Corporates": "ai",
};

/**
 * Reusable lead form. Renders one of these variants:
 *  - "compact"   : hero form, 4 fields
 *  - "course"    : course enquiry, includes course select
 *  - "corporate" : B2B form with company, team size, timeline
 *  - "contact"   : full contact form
 *  - "graduate"  : individual-style form for students/graduates, adds a
 *                  College/University field (Graduate Training page)
 *
 * Pass `formName` to identify the source.
 * All fields are mandatory; email/phone are format-validated.
 */
export default function LeadForm({
  variant: initialVariant = "compact",
  formName = "generic-form",
  defaultCourse = "",
  downloadUrl = "",
  // Noun for the gated document delivered by the "course" variant (the form
  // that hands over a file by email). Drives the button label, the email
  // note and the success message, e.g. "curriculum", "syllabus", "brochure".
  documentNoun = "curriculum",
  className = "",
  surface = "light", // "light" | "dark"
  // When set, the corporate variant's "Training Category" field is locked
  // to this value (e.g. a track landing page always enquiring for "SAP")
  // instead of showing the editable dropdown.
  fixedTrainingCategory = "",
  // Opts a corporate-variant form with an editable (non-fixed) Training
  // Category into the Category → Course cascade -- the Contact Us page and
  // every corporate track page always show it (see showsCorporateCourseField
  // below) regardless of this prop.
  showCourseField = false,
  onSuccess,
}) {
  // "adaptive" starts on individual (compact) and can flip to corporate.
  const isAdaptive = initialVariant === "adaptive";
  const [adaptiveMode, setAdaptiveMode] = useState("Individual");
  const variant = isAdaptive
    ? adaptiveMode === "Corporate"
      ? "corporate"
      : "contact"
    : initialVariant;
  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    course: defaultCourse,
    trainingType: "Individual",
    company: "",
    college: "",
    teamSize: "",
    timeline: "",
    message: "",
    interestedArea: fixedTrainingCategory,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState("US");
  // Ensure the default country (e.g. US) is captured for hero forms even if the
  // user doesn't interact with the country selector. Only set when the data
  // country field is not already populated to avoid overwriting user changes.
  useEffect(() => {
    if (!capturesCountry(formName)) return;
    setData((d) => (d.country ? d : { ...d, country: phoneCountry || "" }));
  }, [formName, phoneCountry]);
  // Keep the locked Training Category in sync with the prop. React Router
  // doesn't remount this component when navigating between track pages
  // (same route element, only the `track` prop changes), so a one-time
  // useState initializer alone would keep showing/submitting whichever
  // track's value happened to be set when this instance first mounted.
  // Also clears whatever course was picked for the previous track, same as
  // onCategoryChange below does for an editable category.
  useEffect(() => {
    if (!fixedTrainingCategory) return;
    setData((d) => (d.interestedArea === fixedTrainingCategory ? d : { ...d, interestedArea: fixedTrainingCategory, course: "" }));
    setCustomCourseText("");
  }, [fixedTrainingCategory]);
  const isCourseDownload = variant === "course" && Boolean(downloadUrl);
  // Every "course" variant form delivers a document by email (curriculum,
  // syllabus or brochure), whether or not an auto-download link is attached.
  const isDocumentForm = variant === "course";
  const docNoun = (documentNoun || "document").trim();
  const docNounCap = docNoun.charAt(0).toUpperCase() + docNoun.slice(1);
  const rid = useId(); // unique id prefix per form instance (avoids duplicate id/label collisions)
  const fid = (name) => `lf-${name}-${rid}`;
  // Dynamic course list , subscribes to backend hydration
  const { courses: LIVE_COURSES } = useCourses();
  const COURSES = LIVE_COURSES;

  // Free-typed course name when the visitor's course isn't in the catalog
  // dropdown for their category (Contact Us page and every corporate track
  // page -- see showsCorporateCourseField below).
  const [customCourseText, setCustomCourseText] = useState("");

  // Category options for the Individual form's new Category dropdown --
  // only categories that actually have a published course, so picking one
  // never leaves the Course dropdown below it empty.
  const availableCategoryIds = useMemo(() => {
    const present = new Set(COURSES.filter((c) => !c.unlisted && c.category).map((c) => c.category));
    const ordered = COURSE_CATEGORIES.map((c) => c.id).filter((id) => present.has(id));
    const extra = [...present].filter((id) => !ordered.includes(id)).sort();
    return [...ordered, ...extra];
  }, [COURSES]);

  // Whether this corporate form shows the Course field below Training
  // Category: the Contact Us page's adaptive corporate form, every
  // corporate track page (fixedTrainingCategory locks the category there,
  // so the course list is simply derived from it directly), and any other
  // corporate form that opts in via showCourseField (e.g. the Corporate
  // Training page and home page's shared corporate enquiry section).
  const showsCorporateCourseField = isAdaptive || Boolean(fixedTrainingCategory) || showCourseField;

  // Courses to list for whichever category is currently selected -- the
  // Individual form's own category (data.interestedArea holds a category
  // label here, see below) or the Corporate form's existing Training
  // Category field, mapped to the matching catalog category id.
  const individualCategoryId = isAdaptive && variant === "contact"
    ? availableCategoryIds.find((id) => categoryLabel(id) === data.interestedArea) || ""
    : "";
  const corporateCategoryId = showsCorporateCourseField && variant === "corporate"
    ? TRAINING_CATEGORY_TO_COURSE_CATEGORY[data.interestedArea] || ""
    : "";
  const coursesForSelectedCategory = useMemo(() => {
    const categoryId = individualCategoryId || corporateCategoryId;
    return categoryId ? getCoursesByCategory(categoryId) : [];
  }, [individualCategoryId, corporateCategoryId, COURSES]);

  const onChange = (k) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setData((d) => ({ ...d, [k]: value }));
    // Live-clear the error for this field as the user edits.
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  // Changing the category invalidates whatever course was previously picked
  // (it belonged to the old category's list), so clear it along with any
  // free-typed "Other" course name.
  const onCategoryChange = (k) => (e) => {
    onChange(k)(e);
    setData((d) => ({ ...d, course: "" }));
    setCustomCourseText("");
    setErrors((prev) => (prev.course ? { ...prev, course: undefined } : prev));
  };

  const validate = () => {
    const errs = {};
    // Common required fields — mandatory across every variant.
    if (!data.name.trim()) errs.name = "This field is required";
    if (!data.email.trim()) {
      errs.email = "This field is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      errs.email = "Invalid email";
    }
    if (!data.phone) {
      errs.phone = "This field is required";
    } else if (!isValidPhoneNumber(data.phone)) {
      errs.phone = "Invalid phone number";
    }

    // Interested Course is mandatory on any variant that shows a course dropdown.
    if ((variant === "compact" || variant === "course") && !data.course) {
      errs.course = "This field is required";
    }

    if (variant === "corporate") {
      if (!data.company.trim()) errs.company = "This field is required";
      if (!data.teamSize) errs.teamSize = "This field is required";
      if (!data.timeline) errs.timeline = "This field is required";
      if (!data.interestedArea.trim()) errs.interestedArea = "This field is required";
      if (!data.message.trim()) errs.message = "This field is required";
      // Course field shown alongside Training Category -- see showsCorporateCourseField.
      if (showsCorporateCourseField) {
        if (!data.course) errs.course = "This field is required";
        else if (data.course === OTHER_COURSE && !customCourseText.trim()) errs.course = "Please type the course name";
      }
    }

    if (variant === "contact") {
      if (!data.interestedArea.trim()) errs.interestedArea = "This field is required";
      if (!data.message.trim()) errs.message = "This field is required";
      // Category → Course cascade only applies to the Contact Us page's
      // adaptive Individual form; other pages using this same variant keep
      // their plain free-text Interested Area field and have no course field.
      if (isAdaptive) {
        if (!data.course) errs.course = "This field is required";
        else if (data.course === OTHER_COURSE && !customCourseText.trim()) errs.course = "Please type the course name";
      }
    }

    if (variant === "graduate") {
      if (!data.college.trim()) errs.college = "This field is required";
      if (!data.interestedArea.trim()) errs.interestedArea = "This field is required";
      if (!data.message.trim()) errs.message = "This field is required";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!validate()) return;
    setSubmitting(true);
    const payload = { ...data, variant };
    // Resolve the Contact Us page's free-typed "Other" course name (see
    // OTHER_COURSE) into the actual value sent to the backend and shown
    // back to the visitor.
    if (payload.course === OTHER_COURSE) payload.course = customCourseText.trim();
    if (fixedTrainingCategory) payload.interestedArea = fixedTrainingCategory;
    // Only send country for hero lead forms (and the leadership enquiry form);
    // convert ISO code to full name.
    if (capturesCountry(formName) && payload.country) {
      try {
        // Use Intl.DisplayNames when available to get localized country name
        if (typeof Intl !== "undefined" && Intl.DisplayNames) {
          const dn = new Intl.DisplayNames(["en"], { type: "region" });
          const full = dn.of(payload.country);
          if (full) payload.country = full;
        } else {
          // Fallback for environments without Intl.DisplayNames: small map
          const FALLBACK = {
            IN: "India",
            US: "United States",
            GB: "United Kingdom",
            LK: "Sri Lanka",
            AU: "Australia",
            CA: "Canada",
            NZ: "New Zealand",
          };
          payload.country = FALLBACK[payload.country] || payload.country;
        }
      } catch (err) {
        // If anything fails, leave the ISO code — better than crashing
      }
    } else {
      delete payload.country;
    }
    const res = await submitLead(payload, { formName });
    setSubmitting(false);
    if (res.ok) {
      setDone(true);
      if (isDocumentForm) {
        toast.success("Check your email", {
          description: `We'll send the ${docNoun} to the email address you entered.`,
        });
      } else {
        toast.success("Thanks! We'll be in touch shortly.", {
          description: "Your enquiry has been received. A training advisor will reach out soon.",
        });
      }
      // Enriches the already-existing (previously unused) onSuccess hook with
      // everything the booking modal needs to prefill its own form --
      // see BookingModal.jsx, wired in at the handful of
      // page-level call sites that want it. Persona comes from which variant
      // actually rendered (so the adaptive Contact Us form reflects whichever
      // toggle the visitor had selected at submit time, not a guess).
      const source = getSourceContext();
      onSuccess?.({
        ...res,
        prefill: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          country: data.country || "",
          persona: variant === "corporate" ? "corporate" : "individual",
          // "course" is the real catalog pick (compact/course variants, and
          // the Contact Us page's Category → Course cascade, already
          // resolved out of OTHER_COURSE via payload.course above);
          // "interestedArea" is the free-text/dropdown field shown as
          // "Interested Area" on contact/graduate variants instead -- fall
          // back to it so the booking modal doesn't show an empty value
          // just because this particular form used the other field.
          courseName: payload.course || data.interestedArea || "",
          courseFieldLabel: (variant === "contact" || variant === "graduate") ? "Interested Area" : "Course Name",
          // For corporate, fall back to whatever Training Category the
          // visitor actually picked (data.interestedArea) when this form
          // doesn't lock one via fixedTrainingCategory -- otherwise the
          // booking modal's Training Category prefill was always empty on
          // any corporate form without a locked category.
          courseCategory: fixedTrainingCategory || (variant === "corporate" ? data.interestedArea : "") || "",
          companyName: variant === "corporate" ? data.company || "" : "",
          teamSize: variant === "corporate" ? data.teamSize || "" : "",
          preferredTimeline: variant === "corporate" ? data.timeline || "" : "",
          formName,
          leadId: res.leadId || null,
          sourcePageUrl: source.source_url || "",
          utmSource: source.utm_source || "",
          utmMedium: source.utm_medium || "",
          utmCampaign: source.utm_campaign || "",
        },
      });
    } else if (res.reason === "cooldown") {
      toast.message("Please wait a moment", {
        description: "We just received your last submission ,  give it a few seconds.",
      });
    } else {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    }
  };

  const isDark = surface === "dark";
  const fieldBase = `w-full rounded-md px-3.5 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 ${isDark
      ? "bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:ring-[var(--cyan)] focus:border-[var(--cyan)]"
      : "bg-white border border-line text-ink placeholder:text-ink-soft/70 focus:ring-royal/30 focus:border-royal"
    }`;
  const labelBase = `block text-[12px] font-semibold mb-1.5 ${isDark ? "text-white/70" : "text-ink"}`;
  const errCls = "mt-1 text-[11px] text-red-500";
  const requiredMark = <span aria-hidden className="ml-0.5 text-red-500">*</span>;

  if (done) {
    return (
      <div
        data-testid={LEAD_FORM.success}
        className={`rounded-xl border ${isDark ? "border-white/10 bg-white/5" : "border-line bg-bg-alt"} p-6 text-center ${className}`}
      >
        <div className="mx-auto h-12 w-12 rounded-full bg-[var(--cyan-tint)] text-[var(--royal-deep)] flex items-center justify-center">
          <CheckCircle2 size={26} />
        </div>
        <h4 className={`mt-4 font-display text-lg font-semibold ${isDark ? "text-white" : "text-ink"}`}>
          {isDocumentForm ? "Check your email" : "Enquiry received"}
        </h4>
        <p className={`mt-1.5 text-sm ${isDark ? "text-white/70" : "text-ink-soft"}`}>
          {isDocumentForm
            ? `We'll send the ${docNoun} to the email address you entered. If you don't see it shortly, check your spam folder.`
            : "A training advisor will reach out to discuss the right program for you."}
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid={LEAD_FORM.root}
      onSubmit={handleSubmit}
      className={`grid gap-3 ${className}`}
      noValidate
    >
      {isAdaptive && (
        <div
          role="radiogroup"
          aria-label="I am enquiring for"
          data-testid="lead-form-adaptive-toggle"
          className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-bg-alt p-1"
        >
          {["Individual", "Corporate"].map((opt) => (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={adaptiveMode === opt}
              data-testid={`lead-form-adaptive-${opt.toLowerCase()}`}
              onClick={() => setAdaptiveMode(opt)}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${adaptiveMode === opt
                  ? "bg-royal-deep text-white shadow-sm"
                  : "text-ink hover:text-royal"
                }`}
            >
              {opt} Training
            </button>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelBase} htmlFor={fid("name")}>
            Full Name{requiredMark}
          </label>
          <input
            id={fid("name")}
            data-testid={LEAD_FORM.name}
            type="text"
            value={data.name}
            onChange={onChange("name")}
            placeholder="Your name"
            className={fieldBase}
            autoComplete="name"
            aria-required="true"
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name && <p className={errCls}>{errors.name}</p>}
        </div>
        <div>
          <label className={labelBase} htmlFor={fid("email")}>
            {variant === "corporate" ? "Work Email" : "Email"}
            {requiredMark}
          </label>
          <input
            id={fid("email")}
            data-testid={LEAD_FORM.email}
            type="email"
            value={data.email}
            onChange={onChange("email")}
            placeholder="you@company.com"
            className={fieldBase}
            autoComplete="email"
            aria-required="true"
            aria-invalid={Boolean(errors.email)}
          />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelBase} htmlFor={fid("phone")}>
            Phone / WhatsApp{requiredMark}
          </label>
          <PhoneField
            id={fid("phone")}
            testId={LEAD_FORM.phone}
            country={phoneCountry}
            onCountryChange={(c) => {
              setPhoneCountry(c);
              setData((d) => ({ ...d, country: c || "" }));
            }}
            value={data.phone}
            onChange={(val) => onChange("phone")(val)}
            placeholder="Enter phone number"
            dark={isDark}
            hasError={Boolean(errors.phone)}
            ariaRequired="true"
          />
          {errors.phone && <p className={errCls}>{errors.phone}</p>}
        </div>

        {variant === "compact" && (
          <div>
            <label className={labelBase} htmlFor={fid("tt")}>
              Training Type{requiredMark}
            </label>
            <select
              id={fid("tt")}
              data-testid={LEAD_FORM.trainingType}
              value={data.trainingType}
              onChange={onChange("trainingType")}
              className={fieldBase}
              aria-required="true"
            >
              <option>Individual</option>
              <option>Corporate</option>
            </select>
          </div>
        )}

        {variant === "course" && (
          <div>
            <label className={labelBase} htmlFor={fid("course")}>
              Interested Course{requiredMark}
            </label>
            <select
              id={fid("course")}
              data-testid={LEAD_FORM.course}
              value={data.course}
              onChange={onChange("course")}
              className={fieldBase}
              aria-required="true"
              aria-invalid={Boolean(errors.course)}
            >
              <option value="">Select a course</option>
              {COURSES.map((c) => (
                <option key={c.slug} value={c.title}>
                  {c.title}
                </option>
              ))}
            </select>
            {errors.course && <p className={errCls}>{errors.course}</p>}
          </div>
        )}

        {variant === "corporate" && (
          <div>
            <label className={labelBase} htmlFor={fid("company")}>
              Company{requiredMark}
            </label>
            <input
              id={fid("company")}
              data-testid={LEAD_FORM.company}
              type="text"
              value={data.company}
              onChange={onChange("company")}
              placeholder="Company name"
              className={fieldBase}
              aria-required="true"
              aria-invalid={Boolean(errors.company)}
            />
            {errors.company && <p className={errCls}>{errors.company}</p>}
          </div>
        )}

        {variant === "contact" && isAdaptive && (
          <div>
            <label className={labelBase} htmlFor={fid("category")}>
              Category{requiredMark}
            </label>
            <select
              id={fid("category")}
              data-testid={LEAD_FORM.category}
              value={data.interestedArea}
              onChange={onCategoryChange("interestedArea")}
              className={fieldBase}
              aria-required="true"
              aria-invalid={Boolean(errors.interestedArea)}
            >
              <option value="">Select a category</option>
              {availableCategoryIds.map((id) => (
                <option key={id} value={categoryLabel(id)}>
                  {categoryLabel(id)}
                </option>
              ))}
            </select>
            {errors.interestedArea && <p className={errCls}>{errors.interestedArea}</p>}
          </div>
        )}

        {(variant === "contact" && !isAdaptive || variant === "graduate") && (
          <div>
            <label className={labelBase} htmlFor={fid("area")}>
              Interested Area{requiredMark}
            </label>
            <input
              id={fid("area")}
              data-testid={LEAD_FORM.course}
              type="text"
              value={data.interestedArea}
              onChange={onChange("interestedArea")}
              placeholder="e.g. SAP AI, Workday, Copilot"
              className={fieldBase}
              aria-required="true"
              aria-invalid={Boolean(errors.interestedArea)}
            />
            {errors.interestedArea && <p className={errCls}>{errors.interestedArea}</p>}
          </div>
        )}
      </div>

      {variant === "contact" && isAdaptive && (
        <div>
          <label className={labelBase} htmlFor={fid("course")}>
            Course{requiredMark}
          </label>
          <select
            id={fid("course")}
            data-testid={LEAD_FORM.course}
            value={data.course}
            onChange={onChange("course")}
            className={fieldBase}
            disabled={!data.interestedArea}
            aria-required="true"
            aria-invalid={Boolean(errors.course)}
          >
            <option value="">
              {data.interestedArea ? "Select a course" : "Select a category first"}
            </option>
            {coursesForSelectedCategory.map((c) => (
              <option key={c.slug} value={c.title}>
                {c.title}
              </option>
            ))}
            <option value={OTHER_COURSE}>Other (not listed)</option>
          </select>
          {data.course === OTHER_COURSE && (
            <input
              data-testid={LEAD_FORM.courseOther}
              type="text"
              value={customCourseText}
              onChange={(e) => {
                setCustomCourseText(e.target.value);
                setErrors((prev) => (prev.course ? { ...prev, course: undefined } : prev));
              }}
              placeholder="Type the course name"
              className={`${fieldBase} mt-2`}
              aria-label="Course name"
            />
          )}
          {errors.course && <p className={errCls}>{errors.course}</p>}
        </div>
      )}

      {variant === "graduate" && (
        <div>
          <label className={labelBase} htmlFor={fid("college")}>
            College / University{requiredMark}
          </label>
          <input
            id={fid("college")}
            data-testid={LEAD_FORM.college}
            type="text"
            value={data.college}
            onChange={onChange("college")}
            placeholder="Your college or university"
            className={fieldBase}
            autoComplete="organization"
            aria-required="true"
            aria-invalid={Boolean(errors.college)}
          />
          {errors.college && <p className={errCls}>{errors.college}</p>}
        </div>
      )}

      {variant === "compact" && (
        <div>
          <label className={labelBase} htmlFor={fid("course-compact")}>
            Interested Course{requiredMark}
          </label>
          <select
            id={fid("course-compact")}
            data-testid={LEAD_FORM.course}
            value={data.course}
            onChange={onChange("course")}
            className={fieldBase}
            aria-required="true"
            aria-invalid={Boolean(errors.course)}
          >
            <option value="">Select a course</option>
            {COURSES.map((c) => (
              <option key={c.slug} value={c.title}>
                {c.title}
              </option>
            ))}
          </select>
          {errors.course && <p className={errCls}>{errors.course}</p>}
        </div>
      )}

      {variant === "corporate" && (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className={labelBase} htmlFor={fid("tsize")}>
                Team Size{requiredMark}
              </label>
              <select
                id={fid("tsize")}
                data-testid={LEAD_FORM.teamSize}
                value={data.teamSize}
                onChange={onChange("teamSize")}
                className={fieldBase}
                aria-required="true"
                aria-invalid={Boolean(errors.teamSize)}
              >
                <option value="">Select size</option>
                <option>1-10</option>
                <option>11-50</option>
                <option>51-200</option>
                <option>200+</option>
              </select>
              {errors.teamSize && <p className={errCls}>{errors.teamSize}</p>}
            </div>
            <div>
              <label className={labelBase} htmlFor={fid("time")}>
                Preferred Timeline{requiredMark}
              </label>
              <select
                id={fid("time")}
                data-testid={LEAD_FORM.timeline}
                value={data.timeline}
                onChange={onChange("timeline")}
                className={fieldBase}
                aria-required="true"
                aria-invalid={Boolean(errors.timeline)}
              >
                <option value="">Select timeline</option>
                <option>Within 1 month</option>
                <option>1-3 months</option>
                <option>3-6 months</option>
                <option>Just exploring</option>
              </select>
              {errors.timeline && <p className={errCls}>{errors.timeline}</p>}
            </div>
            {/* <div>
              <label className={labelBase} htmlFor={fid("req")}>
                Training Category {requiredMark}
              </label>
              <input
                id={fid("req")}
                type="text"
                value={data.interestedArea}
                onChange={onChange("interestedArea")}
                placeholder="e.g. SAP S/4HANA upskill"
                className={fieldBase}
                aria-required="true"
                aria-invalid={Boolean(errors.interestedArea)}
              />
              {errors.interestedArea && <p className={errCls}>{errors.interestedArea}</p>}
            </div> */}
            <div>
              <label className={labelBase} htmlFor={fid("req")}>
                Training Category {requiredMark}
              </label>

              {fixedTrainingCategory ? (
                <input
                  id={fid("req")}
                  type="text"
                  value={fixedTrainingCategory}
                  disabled
                  readOnly
                  className={`${fieldBase} cursor-not-allowed ${isDark ? "bg-white/[0.03] text-white/60" : "bg-bg-alt text-ink-soft"}`}
                  aria-required="true"
                />
              ) : (
                <select
                  id={fid("req")}
                  value={data.interestedArea}
                  onChange={onCategoryChange("interestedArea")}
                  className={fieldBase}
                  aria-required="true"
                  aria-invalid={Boolean(errors.interestedArea)}
                >
                  <option value="">Select Training Category</option>
                  <option value="SAP">SAP</option>
                  <option value="Workday">Workday</option>
                  <option value="AI for Corporates">AI for Corporates</option>
                  <option value="AI for Leaders">AI for Leaders</option>

                </select>
              )}

              {errors.interestedArea && (
                <p className={errCls}>{errors.interestedArea}</p>
              )}
            </div>
          </div>

          {showsCorporateCourseField && (
            <div>
              <label className={labelBase} htmlFor={fid("course")}>
                Course{requiredMark}
              </label>
              <select
                id={fid("course")}
                data-testid={LEAD_FORM.course}
                value={data.course}
                onChange={onChange("course")}
                className={fieldBase}
                disabled={!data.interestedArea}
                aria-required="true"
                aria-invalid={Boolean(errors.course)}
              >
                <option value="">
                  {data.interestedArea ? "Select a course" : "Select a training category first"}
                </option>
                {coursesForSelectedCategory.map((c) => (
                  <option key={c.slug} value={c.title}>
                    {c.title}
                  </option>
                ))}
                <option value={OTHER_COURSE}>Other (not listed)</option>
              </select>
              {data.course === OTHER_COURSE && (
                <input
                  data-testid={LEAD_FORM.courseOther}
                  type="text"
                  value={customCourseText}
                  onChange={(e) => {
                    setCustomCourseText(e.target.value);
                    setErrors((prev) => (prev.course ? { ...prev, course: undefined } : prev));
                  }}
                  placeholder="Type the course name"
                  className={`${fieldBase} mt-2`}
                  aria-label="Course name"
                />
              )}
              {errors.course && <p className={errCls}>{errors.course}</p>}
            </div>
          )}
        </>
      )}

      {(variant === "corporate" || variant === "contact" || variant === "graduate") && (
        <div>
          <label className={labelBase} htmlFor={fid("msg")}>
            Message{requiredMark}
          </label>
          <textarea
            id={fid("msg")}
            data-testid={LEAD_FORM.message}
            value={data.message}
            onChange={onChange("message")}
            rows={4}
            placeholder={
              variant === "corporate"
                ? "Briefly describe your team's training needs"
                : "How can we help?"
            }
            className={fieldBase}
            aria-required="true"
            aria-invalid={Boolean(errors.message)}
          />
          {errors.message && <p className={errCls}>{errors.message}</p>}
        </div>
      )}

      <button
        type="submit"
        data-testid={LEAD_FORM.submit}
        disabled={submitting}
        className={`mt-1 inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold transition-all ${isDark
            ? "bg-[var(--cyan)] text-[var(--royal-deep)] hover:-translate-y-0.5"
            : "bg-royal-deep text-white hover:bg-royal hover:shadow-[0_14px_28px_-14px_rgba(15,42,115,0.6)]"
          } ${submitting ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending…
          </>
        ) : (
          <>
            {isDocumentForm
              ? `Email Me the ${docNounCap}`
              : variant === "corporate"
                ? "Request Custom Proposal"
                : variant === "compact"
                  ? "Get Course Guidance"
                  : "Send Enquiry"}
            {isDocumentForm ? <Mail size={14} /> : <ArrowRight size={14} />}
          </>
        )}
      </button>
      {isDocumentForm && (
        <p className={`flex items-start gap-1.5 text-[11.5px] ${isDark ? "text-white/60" : "text-ink-soft"}`}>
          <Mail size={13} className="mt-0.5 shrink-0 text-royal" aria-hidden="true" />
          <span>We'll send the {docNoun} to the email address you enter above, so please use one you can access.</span>
        </p>
      )}
      <p className={`text-[11px] ${isDark ? "text-white/40" : "text-ink-soft"}`}>
        By submitting, you agree to be contacted by ZaranTech about training programs.
      </p>
    </form>
  );
}
